import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Camera, 
  MapPin, 
  Send,
  X,
  Image as ImageIcon,
  AlertTriangle,
  Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api/v2';

/**
 * MobileRUHNew - Skjema for rask RUH-registrering på mobil
 * Inkluderer kamera og GPS-integrasjon
 */
export default function MobileRUHNew() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isNorwegian = i18n.language === 'nb' || i18n.language === 'no';
  
  const fileInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [location, setLocation] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    ruh_category: 'near_miss',
    immediate_actions: ''
  });

  // Categories for RUH - must match backend RUHCategory enum
  const categories = [
    { value: 'near_miss', label: isNorwegian ? 'Nestenulykke' : 'Near miss' },
    { value: 'personal_injury', label: isNorwegian ? 'Personskade' : 'Personal injury' },
    { value: 'material_damage', label: isNorwegian ? 'Materiell skade' : 'Material damage' },
    { value: 'dangerous_situation', label: isNorwegian ? 'Farlig situasjon' : 'Dangerous situation' },
    { value: 'environmental', label: isNorwegian ? 'Miljøhendelse' : 'Environmental' },
    { value: 'other', label: isNorwegian ? 'Annet' : 'Other' }
  ];

  // Get GPS location
  const getLocation = () => {
    if (!navigator.geolocation) {
      toast.error(isNorwegian ? 'GPS ikke støttet' : 'GPS not supported');
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setLocation({ latitude, longitude });
        
        // Try to get address from coordinates (reverse geocoding)
        fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`)
          .then(res => res.json())
          .then(data => {
            if (data.display_name) {
              setFormData(prev => ({
                ...prev,
                location: data.display_name.split(',').slice(0, 3).join(', ')
              }));
            }
          })
          .catch(() => {
            setFormData(prev => ({
              ...prev,
              location: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
            }));
          })
          .finally(() => setGettingLocation(false));
      },
      (error) => {
        console.error('GPS error:', error);
        toast.error(isNorwegian ? 'Kunne ikke hente posisjon' : 'Could not get location');
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Handle photo capture
  const handlePhotoCapture = (e) => {
    const files = Array.from(e.target.files);
    
    files.forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(isNorwegian ? 'Bildet er for stort (maks 10MB)' : 'Image too large (max 10MB)');
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        setPhotos(prev => [...prev, {
          id: Date.now() + Math.random(),
          file: file,
          preview: event.target.result
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (photoId) => {
    setPhotos(prev => prev.filter(p => p.id !== photoId));
  };

  // Submit RUH
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error(isNorwegian ? 'Beskriv hendelsen' : 'Describe the incident');
      return;
    }

    setSubmitting(true);
    const token = localStorage.getItem('systemklart_token');

    // Check if online
    if (!navigator.onLine) {
      // Save to local storage for later sync
      const pendingRUH = JSON.parse(localStorage.getItem('pending_ruh') || '[]');
      pendingRUH.push({
        id: Date.now(),
        token: token,
        data: {
          type: 'ruh',
          ...formData,
          incident_datetime: new Date().toISOString()
        },
        photos: photos.map(p => p.preview),
        created_at: new Date().toISOString()
      });
      localStorage.setItem('pending_ruh', JSON.stringify(pendingRUH));
      
      toast.success(isNorwegian ? 'Lagret lokalt - synkroniseres når du er online' : 'Saved locally - will sync when online');
      navigate('/m/ruh');
      return;
    }

    try {
      // Create RUH
      const response = await axios.post(`${API}/ops/deviations`, {
        type: 'ruh',
        title: formData.title || formData.description.slice(0, 50),
        description: formData.description,
        location: formData.location,
        ruh_category: formData.ruh_category,
        immediate_actions: formData.immediate_actions,
        incident_datetime: new Date().toISOString(),
        severity: 'low'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const ruhId = response.data.id;

      // Upload photos if any
      for (const photo of photos) {
        const photoFormData = new FormData();
        photoFormData.append('file', photo.file);
        
        try {
          await axios.post(`${API}/ops/deviations/${ruhId}/files`, photoFormData, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });
        } catch (err) {
          console.error('Photo upload failed:', err);
        }
      }

      toast.success(isNorwegian ? 'RUH registrert!' : 'RUH registered!');
      navigate('/m/ruh');
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(isNorwegian ? 'Kunne ikke registrere RUH' : 'Could not register RUH');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    navigate('/m/ruh/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col" data-testid="mobile-ruh-new">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900 border-b border-slate-800 safe-area-top">
        <div className="px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/m/ruh')}
              className="p-2 -ml-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-white">
                {isNorwegian ? 'Ny RUH' : 'New RUH'}
              </h1>
              <p className="text-xs text-slate-500">
                {isNorwegian ? 'Registrer uønsket hendelse' : 'Register incident'}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Form */}
      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-4">
          {/* Category selector */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              {isNorwegian ? 'Type hendelse' : 'Incident type'}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, ruh_category: cat.value }))}
                  className={`p-3 rounded-xl text-sm font-medium transition-all ${
                    formData.ruh_category === cat.value
                      ? 'bg-cyan-500 text-slate-950'
                      : 'bg-slate-800 text-slate-300 border border-slate-700 hover:border-slate-600'
                  }`}
                  data-testid={`category-${cat.value}`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              {isNorwegian ? 'Hva skjedde?' : 'What happened?'} *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={4}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 resize-none"
              placeholder={isNorwegian ? 'Beskriv hendelsen...' : 'Describe the incident...'}
              required
              data-testid="description-input"
            />
          </div>

          {/* Location with GPS */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              {isNorwegian ? 'Sted' : 'Location'}
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                className="flex-1 px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500"
                placeholder={isNorwegian ? 'Hvor skjedde det?' : 'Where did it happen?'}
                data-testid="location-input"
              />
              <button
                type="button"
                onClick={getLocation}
                disabled={gettingLocation}
                className="p-3 bg-slate-800 border border-slate-700 rounded-xl text-slate-400 hover:text-cyan-400 hover:border-cyan-500/50 transition-colors"
                data-testid="gps-btn"
              >
                {gettingLocation ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <MapPin className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          {/* Immediate actions */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              {isNorwegian ? 'Strakstiltak' : 'Immediate actions'}
            </label>
            <textarea
              value={formData.immediate_actions}
              onChange={(e) => setFormData(prev => ({ ...prev, immediate_actions: e.target.value }))}
              rows={2}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 resize-none"
              placeholder={isNorwegian ? 'Hva ble gjort umiddelbart?' : 'What was done immediately?'}
              data-testid="immediate-actions-input"
            />
          </div>

          {/* Photo section */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              {isNorwegian ? 'Bilder' : 'Photos'}
            </label>
            
            {/* Photo previews */}
            {photos.length > 0 && (
              <div className="flex gap-2 mb-3 overflow-x-auto pb-2">
                {photos.map((photo) => (
                  <div key={photo.id} className="relative flex-shrink-0">
                    <img
                      src={photo.preview}
                      alt="Preview"
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(photo.id)}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-rose-500 rounded-full flex items-center justify-center text-white"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Camera buttons */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  fileInputRef.current.setAttribute('capture', 'environment');
                  fileInputRef.current.click();
                }}
                className="flex-1 py-3 bg-slate-800 border border-slate-700 rounded-xl text-slate-300 hover:border-cyan-500/50 transition-colors flex items-center justify-center gap-2"
                data-testid="camera-btn"
              >
                <Camera className="w-5 h-5" />
                {isNorwegian ? 'Ta bilde' : 'Take photo'}
              </button>
              <button
                type="button"
                onClick={() => {
                  fileInputRef.current.removeAttribute('capture');
                  fileInputRef.current.click();
                }}
                className="flex-1 py-3 bg-slate-800 border border-slate-700 rounded-xl text-slate-300 hover:border-cyan-500/50 transition-colors flex items-center justify-center gap-2"
                data-testid="gallery-btn"
              >
                <ImageIcon className="w-5 h-5" />
                {isNorwegian ? 'Velg bilde' : 'Choose photo'}
              </button>
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoCapture}
              className="hidden"
              multiple
            />
          </div>
        </div>

        {/* Submit button */}
        <div className="sticky bottom-0 p-4 bg-slate-950 border-t border-slate-800 safe-area-bottom">
          <button
            type="submit"
            disabled={submitting || !formData.description.trim()}
            className="w-full py-4 bg-cyan-500 text-slate-950 rounded-xl font-semibold hover:bg-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-cyan-500/25 flex items-center justify-center gap-2"
            data-testid="submit-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {isNorwegian ? 'Sender...' : 'Submitting...'}
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                {isNorwegian ? 'Send RUH' : 'Submit RUH'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
